import { defineStore, storeToRefs } from 'pinia';
import { ref, computed, watch } from 'vue';
import { useBundleBuilder } from '../composables/useBundleBuilder';
import { useModalStore } from '@/features/UserInterface/stores/modal';
import { useProductStockStore } from './productStock';
import type { BundleGroupInfo, ProductComplete } from '@/features/Product/types/models';

export const useProductPageBundleStore = defineStore('productPageBundle', () => {
  const currentProduct = ref<ProductComplete | null>(null);
  const bundleBuilder = ref<ReturnType<typeof useBundleBuilder> | null>(null);
  const isAddingToCart = ref(false);
  const isInitializing = ref(false);
  const bundleModal = ref<string | null>(null);
  const selectionStockNotices = ref<{ title: string; isUnavailable: boolean }[]>([]);

  const { openModal, closeModal } = useModalStore();
  const { variantStock } = storeToRefs(useProductStockStore());

  watch(variantStock, () => {
    const trimmedSelections = bundleBuilder.value?.removeSoldOutSelections() ?? [];
    trimmedSelections.forEach(({ product, remainingQuantity }) => {
      const title = product.content.title;
      const isUnavailable = remainingQuantity === 0;
      const existingNotice = selectionStockNotices.value.find((notice) => notice.title === title);
      if (existingNotice) existingNotice.isUnavailable = isUnavailable;
      else selectionStockNotices.value.push({ title, isUnavailable });
    });
  });

  const clearSelectionStockNotice = () => {
    selectionStockNotices.value = [];
  };

  const initialiseBundle = (
    product: ProductComplete,
    options?: { autoSelectDefaults?: boolean; resolvePoolPlaceholder?: boolean },
  ) => {
    // Is it worth clearing the bundle at the start of every initalise to ensure clean state?
    clearBundle();
    isInitializing.value = true;
    bundleModal.value = 'bundle-group-select';

    try {
      currentProduct.value = product;

      if (!product?.bundleDefinition) throw new Error('Product does not have a bundle definition');
      bundleBuilder.value = useBundleBuilder({
        bundleDefinition: product.bundleDefinition,
        autoSelectDefaults: options?.autoSelectDefaults ?? true,
        mode: 'full',
        resolvePoolPlaceholder: options?.resolvePoolPlaceholder,
      });

      return true;
    } catch (error) {
      console.error('Failed to initialise bundle because...', error);
      return false;
    } finally {
      isInitializing.value = false;
    }
  };

  const initialiseBundlePreservingSelections = (
    product: ProductComplete,
    options?: { autoSelectDefaults?: boolean; resolvePoolPlaceholder?: boolean },
  ) => {
    const existingSelections =
      bundleBuilder.value?.selectedProducts.map((selection) => ({
        product: selection.product,
        quantity: selection.quantity,
      })) ?? [];
    const didInitialise = initialiseBundle(product, options);
    if (!didInitialise || !bundleBuilder.value) return false;
    const groups = bundleBuilder.value.getBundleGroups();
    const firstChoiceGroup = groups.find((group) => group.type === 'choice_group');
    if (!firstChoiceGroup) return true;
    existingSelections.forEach((selection) => {
      for (let i = 0; i < selection.quantity; i++) {
        bundleBuilder.value?.addToGroup(firstChoiceGroup.key, selection.product, 1);
      }
    });
    return true;
  };

  // Clear bundle so store is fresh when leaving page
  const clearBundle = () => {
    clearSelectionStockNotice();
    currentProduct.value = null;
    bundleBuilder.value = null;
    isAddingToCart.value = false;
  };

  const resetBundle = () => {
    if (currentProduct.value && currentProduct.value.bundleDefinition) {
      bundleBuilder.value = useBundleBuilder({
        bundleDefinition: currentProduct.value.bundleDefinition,
        autoSelectDefaults: true,
        mode: 'full',
      });
    }
  };

  const updatedBundleAtcStructure = computed(() => {
    return bundleBuilder?.value?.buildCartObject(currentProduct.value) || null;
  });

  // Selection actions - can add error messages here too
  const addToGroup = (groupKey: string, product: ProductComplete) => {
    if (!bundleBuilder.value) return false;
    clearSelectionStockNotice();

    return bundleBuilder.value.addToGroup(groupKey, product);
  };

  const removeFromGroup = (groupKey: string, variantId: number) => {
    if (!bundleBuilder.value) return false;
    clearSelectionStockNotice();

    return bundleBuilder.value.removeFromGroup(groupKey, variantId);
  };

  const clearGroup = (groupKey: string) => {
    if (!bundleBuilder.value) return false;

    bundleBuilder.value.clearGroup(groupKey);
  };

  const toggleProduct = (groupKey: string, product: ProductComplete) => {
    if (!bundleBuilder.value) return false;

    const groups = bundleBuilder.value.getBundleGroups();
    const group = groups.find((group) => group.key === groupKey);
    const isSelected = group?.options.find(
      (option) => option.product.attributes.variantId === product.attributes.variantId,
    )?.isSelected;

    if (isSelected) {
      return removeFromGroup(groupKey, product.attributes.variantId);
    } else {
      return addToGroup(groupKey, product);
    }
  };

  // ! We are wrapping the composable with the store to hopefully create a better DX
  const selectedProducts = computed(() => bundleBuilder.value?.selectedProducts || []);

  const totalPrice = computed(() => bundleBuilder.value?.totalPrice || 0);

  const fromPrice = computed(() => bundleBuilder.value?.fromPrice || 0);

  const isValid = computed(() => bundleBuilder.value?.isValidBundle || false);

  const totalQuantity = computed(() => bundleBuilder.value?.totalQuantity || 0);

  const validationState = computed(
    () =>
      bundleBuilder.value?.validationState || {
        isValid: false,
        errors: [],
        warnings: [],
        totalSelected: 0,
      },
  );

  const bundleGroups = computed(() => bundleBuilder.value?.getBundleGroups() || []);

  const isFixedBundle = computed(
    () => bundleGroups.value.length > 0 && bundleGroups.value.every((g) => g.type === 'fixed'),
  );

  const hasBundle = computed(() => !!currentProduct.value?.bundleDefinition);

  const bundleTitle = computed(() => currentProduct.value?.bundleDefinition?.title || '');

  const bundleType = computed(() => currentProduct.value?.bundleDefinition?.bundleType);

  const canAddToCart = computed(() => isValid.value && !isAddingToCart.value);

  const selectionProgress = computed(() => {
    if (!bundleBuilder.value?.bundleDefinition) return null;

    const total = validationState.value.totalSelected;
    const min = bundleBuilder.value.bundleDefinition.minItems;
    const max = bundleBuilder.value.bundleDefinition.maxItems;

    return {
      current: total,
      minimum: min,
      maximum: max,
      percentage: max ? (total / max) * 100 : 0,
      remaining: min ? Math.max(0, min - total) : 0,
    };
  });

  const addToCartMessage = computed(() => {
    if (isAddingToCart.value) return 'Adding to cart...';
    return `Add Bundle to cart`;
  });

  const incrementProduct = (groupKey: string, product: ProductComplete) => {
    clearSelectionStockNotice();
    return bundleBuilder.value?.addToGroup(groupKey, product, 1) || false;
  };

  const decrementProduct = (groupKey: string, variantId: number) => {
    clearSelectionStockNotice();
    return bundleBuilder.value?.removeFromGroup(groupKey, variantId, 1) || false;
  };

  const setProductQuantity = (groupKey: string, product: ProductComplete, quantity: number) => {
    clearSelectionStockNotice();
    return bundleBuilder.value?.setProductQuantity(groupKey, product, quantity) || false;
  };

  const getProductQuantity = (groupKey: string, variantId: number) => {
    return bundleBuilder.value?.getProductQuantity(groupKey, variantId) ?? 0;
  };

  const getGroupOption = (groupKey: string, product: ProductComplete) => {
    return bundleBuilder.value?.getGroupOption(groupKey, product);
  };

  const openGroupSelection = (groupKey: string, index: number) => {
    bundleBuilder.value.setActiveGroup(groupKey, index);
    openModal(bundleModal.value);
  };

  const closeGroupSelection = () => {
    bundleBuilder.value.clearActiveGroup();
    closeModal(bundleModal.value);
  };

  const activeGroupKey = computed(() => {
    return bundleBuilder.value?.activeGroupKey;
  });

  const activeGroupIndex = computed(() => {
    return bundleBuilder.value?.activeGroupIndex;
  });

  const activeGroup = computed(() => {
    return bundleBuilder.value?.activeGroup;
  });

  const canAddToGroup = computed(() => bundleBuilder.value?.canAddToGroup);

  const getGroupSelections = (group: BundleGroupInfo) => {
    return bundleBuilder.value?.getGroupSelections(group);
  };

  const handleGroupSelection = (product: ProductComplete): boolean => {
    const addProduct = bundleBuilder.value?.handleGroupSelection(product);
    if (activeGroup.value?.maxSelection === 1) {
      closeGroupSelection();
    }
    return addProduct || false;
  };

  return {
    currentProduct,
    isAddingToCart,
    selectionStockNotices,
    clearSelectionStockNotice,
    isInitializing,
    hasBundle,
    bundleTitle,
    bundleType,
    activeGroupKey,
    activeGroupIndex,
    activeGroup,
    canAddToGroup,
    getGroupSelections,
    initialiseBundle,
    initialiseBundlePreservingSelections,
    clearBundle,
    resetBundle,
    incrementProduct,
    decrementProduct,
    setProductQuantity,
    getProductQuantity,
    getGroupOption,
    addToGroup,
    removeFromGroup,
    clearGroup,
    toggleProduct,
    openGroupSelection,
    closeGroupSelection,
    handleGroupSelection,
    selectedProducts,
    totalPrice,
    fromPrice,
    isValid,
    validationState,
    bundleGroups,
    isFixedBundle,
    totalQuantity,
    updatedBundleAtcStructure,
    canAddToCart,
    selectionProgress,
    addToCartMessage,
  };
});
